import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, PaperPlaneTilt, X as XIcon, Sparkle, CheckCircle,
  Clock, EnvelopeSimple, EyeSlash, Play, Eye, CursorClick,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Fixed 7-step trial nurture cadence — mirrors backend NURTURE_CONFIG.
const STEP_LABELS = {
  day1:  { title: "Day 1 — Welcome",        short: "Day 1",  color: "text-emerald-400", hours: 24  },
  day3:  { title: "Day 3 — AI Negotiator",  short: "Day 3",  color: "text-orange-400",  hours: 72  },
  day5:  { title: "Day 5 — PDF Invoicing",  short: "Day 5",  color: "text-cyan-400",    hours: 120 },
  day7:  { title: "Day 7 — Mid-trial",      short: "Day 7",  color: "text-blue-400",    hours: 168 },
  day10: { title: "Day 10 — Broker wins",   short: "Day 10", color: "text-violet-400",  hours: 240 },
  day12: { title: "Day 12 — Trial ending",  short: "Day 12", color: "text-red-400",     hours: 288 },
  day14: { title: "Day 14 — Last chance",   short: "Day 14", color: "text-rose-400",    hours: 336 },
};
const STEP_KEYS = ["day1", "day3", "day5", "day7", "day10", "day12", "day14"];

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function ComposeModal({ open, candidate, onClose, onSent, adminEmail }) {
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [replyTo, setReplyTo] = useState(adminEmail || "");

  useEffect(() => {
    if (open) {
      setDraft(null);
      setReplyTo(adminEmail || "");
    }
  }, [open, adminEmail]);

  if (!open || !candidate) return null;

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await client.post("/admin/nurture/draft", {
        user_id: candidate.user.id,
        step_key: candidate.step_key,
      });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!draft?.subject || !draft?.body) {
      toast.error("Generate a draft first");
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post("/admin/nurture/send", {
        user_id: candidate.user.id,
        step_key: candidate.step_key,
        subject: draft.subject,
        body: draft.body,
        reply_to: replyTo || undefined,
      });
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " (via your Gmail)" :
                    data.provider === "resend" ? " (via Resend)" : "";
        toast.success(`Sent to ${candidate.user.email}${via}`);
        onSent?.();
        onClose();
      } else {
        toast.error(data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const stepInfo = STEP_LABELS[candidate.step_key] || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="nurture-compose-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                Trial nurture — {stepInfo.title}
              </div>
              <div className="text-sm text-white font-medium">
                {candidate.user.full_name || "—"} · {candidate.user.company_name || "—"}
              </div>
              <div className="text-xs text-neutral-500 font-mono">{candidate.user.email}</div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white p-1"
                    data-testid="nurture-modal-close">
              <XIcon size={18} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Context */}
            <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 text-xs text-neutral-400">
              <span className="text-neutral-300">{fmtHours(candidate.user.hours_since_signup)}</span> since signup
              {candidate.user.trial_ends_at && (
                <> · trial ends <span className="text-neutral-300">{new Date(candidate.user.trial_ends_at).toLocaleDateString()}</span></>
              )}
              {candidate.user.subscription_status && (
                <> · status <span className="text-neutral-300">{candidate.user.subscription_status}</span></>
              )}
            </div>

            {/* Reply-to */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold block mb-1.5">
                Reply-to email
              </label>
              <input
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="michael@freightpilotai.ai"
                className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none font-mono"
                data-testid="nurture-reply-to-input"
              />
            </div>

            {/* Generate button */}
            {!draft && (
              <button
                onClick={generate}
                disabled={drafting}
                data-testid="nurture-generate-btn"
                className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60"
              >
                {drafting ? (
                  <><ArrowClockwise size={12} className="animate-spin" /> AI drafting…</>
                ) : (
                  <><Sparkle size={12} weight="fill" /> Generate AI draft</>
                )}
              </button>
            )}

            {/* Draft preview */}
            {draft && (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold block mb-1.5">
                    Subject
                  </label>
                  <input
                    value={draft.subject}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                    className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none"
                    data-testid="nurture-subject-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold block mb-1.5">
                    Body
                  </label>
                  <textarea
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    rows={11}
                    className="w-full rounded-md bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#FF5A1F] outline-none font-mono"
                    data-testid="nurture-body-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={generate} disabled={drafting}
                          className="rounded-full px-4 py-2 text-xs border border-white/15 hover:border-white/30 text-neutral-300 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-60"
                          data-testid="nurture-regenerate-btn">
                    <ArrowClockwise size={12} className={drafting ? "animate-spin" : ""} /> Regenerate
                  </button>
                  <button onClick={send} disabled={sending}
                          className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60"
                          data-testid="nurture-send-btn">
                    <PaperPlaneTilt size={12} weight="fill" /> {sending ? "Sending…" : "Send now"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminNurturePage() {
  const { user } = useAuth();
  const adminEmail = user?.email || "";
  const [tab, setTab] = useState("due");  // due | sent
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [perStep, setPerStep] = useState({});
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [abReport, setAbReport] = useState(null);
  const [abLocks, setAbLocks] = useState([]);
  const [composeFor, setComposeFor] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, s, ab, locks] = await Promise.all([
        client.get("/admin/nurture/candidates"),
        client.get("/admin/nurture/history"),
        client.get("/admin/nurture/scheduler-status"),
        client.get("/admin/nurture/ab-report"),
        client.get("/admin/nurture/ab-locks"),
      ]);
      setCandidates(c.data.candidates || []);
      setPerStep(c.data.per_step || {});
      setHistory(h.data.emails || []);
      setStatus(s.data || null);
      setAbReport(ab.data || null);
      setAbLocks(locks.data?.locks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load nurture data");
    } finally {
      setLoading(false);
    }
  };

  const lockAbVariant = async (stepKey, variantIndex) => {
    try {
      await client.post(`/admin/nurture/ab-lock/${stepKey}/${variantIndex}`);
      toast.success(`Locked variant ${String.fromCharCode(65 + variantIndex)} for ${stepKey}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock failed");
    }
  };

  const unlockAbStep = async (stepKey) => {
    try {
      await client.delete(`/admin/nurture/ab-lock/${stepKey}`);
      toast.success(`Unlocked ${stepKey} — resumed A/B split`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    }
  };

  useEffect(() => { load(); }, []);

  const runBatch = async () => {
    const total = candidates.length;
    if (!total) {
      toast.info("No trial users due right now");
      return;
    }
    if (!window.confirm(`Send AI-drafted trial nurture emails to ${total} user(s) now?`)) return;
    setRunning(true);
    try {
      const { data } = await client.post("/admin/nurture/run-batch");
      toast.success(`Processed ${data.processed}: ${data.sent} sent, ${data.failed} failed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch run failed");
    } finally {
      setRunning(false);
    }
  };

  const skip = async (uid, name) => {
    if (!window.confirm(`Permanently skip ${name || "this user"} from all future nurture emails?`)) return;
    try {
      await client.post(`/admin/nurture/skip/${uid}`);
      toast.success("User skipped");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-nurture-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">TRIAL NURTURE</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Trial nurture emails
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Fixed 7-touch cadence to keep trial users engaged and convert to paid. Day 1 welcome,
            day 3 AI Negotiator, day 5 branded invoicing, day 7 mid-trial, day 10 broker wins,
            day 12 upgrade nudge, day 14 last-chance. Runs autonomously every hour — this page
            lets you preview + send manually.
          </p>
        </div>
        <button
          onClick={runBatch}
          disabled={running || loading}
          data-testid="nurture-run-batch-btn"
          className="btn-primary rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-60"
        >
          {running ? (
            <><ArrowClockwise size={12} className="animate-spin" /> Running…</>
          ) : (
            <><Play size={12} weight="fill" /> Run batch now</>
          )}
        </button>
      </div>

      {/* Autonomous scheduler status */}
      {status && (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-4 flex-wrap"
          data-testid="nurture-scheduler-status"
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${
                status.running
                  ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/5"
                  : "text-neutral-500 border-neutral-500/30 bg-neutral-500/5"
              }`}
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  status.running ? "bg-emerald-400 animate-pulse" : "bg-neutral-500"
                }`}
              />
              {status.running ? "Auto-send LIVE" : "Auto-send OFF"}
            </span>
            <div className="text-xs text-neutral-400">
              {status.pending_candidates > 0
                ? `${status.pending_candidates} email${status.pending_candidates === 1 ? "" : "s"} queued for next batch`
                : "Queue empty — no one due right now"}
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-neutral-500">
            <div>
              <span className="text-neutral-400">Last run:</span>{" "}
              <span className="tabular-nums text-neutral-300">
                {status.last_run_at
                  ? new Date(status.last_run_at).toLocaleString()
                  : "Warming up…"}
              </span>
            </div>
            <div>
              <span className="text-neutral-400">Cadence:</span>{" "}
              <span className="tabular-nums text-neutral-300">
                every {Math.round((status.interval_seconds || 3600) / 60)}m
              </span>
            </div>
            <div>
              <span className="text-neutral-400">Cap:</span>{" "}
              <span className="tabular-nums text-neutral-300">{status.batch_cap}/batch</span>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards per step */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="nurture-stats">
        {STEP_KEYS.map((k) => {
          const info = STEP_LABELS[k];
          return (
            <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
                 data-testid={`nurture-stat-${k}`}>
              <div className={`text-[10px] uppercase tracking-widest ${info.color} font-semibold mb-1`}>
                {info.short}
              </div>
              <div className="font-display text-3xl font-semibold tabular-nums">
                {perStep[k] ?? 0}
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                {info.title} · +{fmtHours(info.hours)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10">
        <button
          onClick={() => setTab("due")}
          data-testid="nurture-tab-due"
          className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
            tab === "due"
              ? "border-[#FF5A1F] text-white"
              : "border-transparent text-neutral-500 hover:text-neutral-300"
          }`}
        >
          Due ({candidates.length})
        </button>
        <button
          onClick={() => setTab("sent")}
          data-testid="nurture-tab-sent"
          className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
            tab === "sent"
              ? "border-[#FF5A1F] text-white"
              : "border-transparent text-neutral-500 hover:text-neutral-300"
          }`}
        >
          Sent ({history.length})
        </button>
        <button
          onClick={() => setTab("ab")}
          data-testid="nurture-tab-ab"
          className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
            tab === "ab"
              ? "border-[#FF5A1F] text-white"
              : "border-transparent text-neutral-500 hover:text-neutral-300"
          }`}
        >
          A/B report
        </button>
      </div>

      {/* Due list */}
      {tab === "due" && (
        <div className="space-y-2" data-testid="nurture-due-list">
          {loading ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>
          ) : candidates.length === 0 ? (
            <div className="text-sm text-neutral-500 py-12 text-center border border-dashed border-white/10 rounded-lg">
              <CheckCircle size={20} className="mx-auto mb-2 text-emerald-400" />
              All caught up — no trial users due for a nurture email right now.
            </div>
          ) : (
            candidates.map((c) => {
              const info = STEP_LABELS[c.step_key] || {};
              return (
                <div key={`${c.user.id}-${c.step_key}`}
                     className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3 flex-wrap"
                     data-testid={`nurture-candidate-${c.user.id}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${info.color} font-semibold shrink-0 w-16`}>
                    {info.short}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">
                      {c.user.full_name || "—"} · <span className="text-neutral-400">{c.user.company_name || "no company"}</span>
                    </div>
                    <div className="text-xs text-neutral-500 font-mono truncate">{c.user.email}</div>
                  </div>
                  <div className="text-xs text-neutral-400 shrink-0">
                    <Clock size={12} weight="fill" className="inline mr-1" />
                    {fmtHours(c.user.hours_since_signup)} since signup
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => setComposeFor(c)}
                      data-testid={`nurture-draft-btn-${c.user.id}`}
                      className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                    >
                      <Sparkle size={11} weight="fill" /> Draft + Send
                    </button>
                    <button
                      onClick={() => skip(c.user.id, c.user.full_name)}
                      data-testid={`nurture-skip-btn-${c.user.id}`}
                      className="p-1.5 text-neutral-500 hover:text-red-400 transition-colors"
                      title="Skip permanently"
                    >
                      <EyeSlash size={14} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Sent list */}
      {tab === "sent" && (
        <div className="space-y-2" data-testid="nurture-sent-list">
          {loading ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-neutral-500 py-12 text-center border border-dashed border-white/10 rounded-lg">
              <EnvelopeSimple size={20} className="mx-auto mb-2 text-neutral-600" />
              No nurture emails sent yet.
            </div>
          ) : (
            history.map((h) => {
              const info = STEP_LABELS[h.step_key] || {};
              const opened = (h.open_count || 0) > 0;
              const clicked = (h.click_count || 0) > 0;
              return (
                <div key={h.id}
                     className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3 flex-wrap"
                     data-testid={`nurture-sent-${h.id}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${info.color} font-semibold shrink-0 w-16`}>
                    {info.short}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{h.subject}</div>
                    <div className="text-xs text-neutral-500 font-mono truncate">
                      → {h.user_email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    {h.status === "sent" ? (
                      <span className="text-emerald-400 inline-flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" /> sent
                      </span>
                    ) : (
                      <span className="text-red-400 inline-flex items-center gap-1">
                        <XIcon size={12} weight="bold" /> {h.status}
                      </span>
                    )}
                    {opened && (
                      <span className={`inline-flex items-center gap-1 ${clicked ? "text-orange-400" : "text-emerald-400"}`}
                            title={`Opened ${h.open_count}x`}>
                        <Eye size={12} weight="fill" /> {h.open_count}
                      </span>
                    )}
                    {clicked && (
                      <span className="inline-flex items-center gap-1 text-orange-400"
                            title={`Clicked ${h.click_count}x`}>
                        <CursorClick size={12} weight="fill" /> {h.click_count}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-600 shrink-0 font-mono">
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* A/B report */}
      {tab === "ab" && (
        <div className="space-y-3" data-testid="nurture-ab-list">
          {loading || !abReport ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Loading A/B data…</div>
          ) : (
            <>
              <div className="text-[11px] text-neutral-500 mb-1">
                Winner declared when a variant hits ≥{abReport.min_sends_for_winner} sends
                AND beats the loser by ≥{abReport.min_gap_pct_for_winner}pp on open rate.
              </div>
              {abReport.steps.map((step) => {
                const info = STEP_LABELS[step.step_key] || {};
                const winner = step.winner_variant_index;
                const lock = abLocks.find((l) => l.step_key === step.step_key);
                const lockedIdx = lock?.variant_index;
                return (
                  <div
                    key={step.step_key}
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`nurture-ab-step-${step.step_key}`}
                  >
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className={`text-[10px] uppercase tracking-widest ${info.color} font-semibold`}>
                        {info.short} · {info.title}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-neutral-500 tabular-nums">
                        {step.confidence && (
                          <ConfidenceChip conf={step.confidence} stepKey={step.step_key} />
                        )}
                        {lock && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-[#FF9764] font-semibold uppercase tracking-widest text-[9px]"
                            data-testid={`nurture-ab-locked-${step.step_key}`}
                            title={`Locked by ${lock.locked_by}`}
                          >
                            <Sparkle size={9} weight="fill" /> Locked · Var {String.fromCharCode(65 + lock.variant_index)}
                          </span>
                        )}
                        <span>
                          {step.total_sent} total sent
                          {step.edited_sent > 0 && (
                            <span className="text-neutral-600"> · {step.edited_sent} edited</span>
                          )}
                        </span>
                        {lock && (
                          <button
                            onClick={() => unlockAbStep(step.step_key)}
                            data-testid={`nurture-ab-unlock-${step.step_key}`}
                            className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white underline underline-offset-2"
                          >
                            Unlock
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {step.variants.map((v) => {
                        const isWinner = winner === v.variant_index;
                        const isLocked = lockedIdx === v.variant_index;
                        return (
                          <div
                            key={v.variant_index}
                            className={`rounded-md border p-3 text-xs ${
                              isLocked
                                ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/5"
                                : isWinner
                                ? "border-emerald-400/40 bg-emerald-400/5"
                                : "border-white/10 bg-black/20"
                            }`}
                            data-testid={`nurture-ab-variant-${step.step_key}-${v.variant_index}`}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                                Variant {String.fromCharCode(65 + v.variant_index)}
                              </span>
                              {isLocked ? (
                                <span className="text-[9px] uppercase tracking-widest text-[#FF9764] font-semibold">
                                  Live
                                </span>
                              ) : isWinner ? (
                                <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-semibold">
                                  Winning
                                </span>
                              ) : null}
                            </div>
                            <div className="text-neutral-200 leading-snug mb-2">"{v.subject}"</div>
                            {v.body_angle && (
                              <div className="text-[10px] text-neutral-500 leading-snug mb-2 italic line-clamp-2" title={v.body_angle}>
                                Body angle: {v.body_angle}
                              </div>
                            )}
                            <div className="flex items-center gap-3 text-[11px] text-neutral-500 tabular-nums mb-2">
                              <span>{v.sent} sent</span>
                              <span className={v.open_rate_pct > 0 ? "text-neutral-300" : ""}>
                                {v.open_rate_pct}% open
                              </span>
                              <span className={v.click_rate_pct > 0 ? "text-neutral-300" : ""}>
                                {v.click_rate_pct}% click
                              </span>
                            </div>
                            {!lock && (
                              <button
                                onClick={() => lockAbVariant(step.step_key, v.variant_index)}
                                data-testid={`nurture-ab-lock-${step.step_key}-${v.variant_index}`}
                                className="w-full text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-2.5 py-1 transition-colors"
                              >
                                Lock this variant
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      <ComposeModal
        open={!!composeFor}
        candidate={composeFor}
        onClose={() => setComposeFor(null)}
        onSent={load}
        adminEmail={adminEmail}
      />
    </div>
  );
}


// Confidence chip for the A/B report — two-proportion z-test on open rates.
// Colors:
//   ≥95%  emerald (winner clear)
//   ≥80%  amber   (leaning)
//   too_early / <80  neutral (gather more data)
function ConfidenceChip({ conf, stepKey }) {
  if (!conf) return null;
  const { confidence_pct, too_early, label } = conf;
  const tone = too_early || confidence_pct == null
    ? "border-white/10 bg-white/[0.03] text-neutral-500"
    : confidence_pct >= 95
    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
    : confidence_pct >= 80
    ? "border-amber-300/40 bg-amber-300/10 text-amber-200"
    : "border-white/10 bg-white/[0.03] text-neutral-400";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${tone}`}
      data-testid={`nurture-ab-confidence-${stepKey}`}
      title={label}
    >
      {label}
    </span>
  );
}
